import React, { useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { ArrowLeft, Heart, User, Mail, Send, Check } from 'lucide-react';
import { motion } from 'framer-motion';
import { useAuth } from '../../store/authStore';
import { shareReport } from '../../api/reports';
import { InputField } from '../../components/InputField';
import { Button } from '../../components/Button';
export function ShareReport() {
  const {
    sessionId
  } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    token,
    user
  } = useAuth();
  // State from previous screen
  const {
    avgBpm,
    minBpm,
    maxBpm,
    totalReadings
  } = location.state || {
    avgBpm: '--',
    minBpm: '--',
    maxBpm: '--',
    totalReadings: '--'
  };
  const [doctorName, setDoctorName] = useState('');
  const [doctorEmail, setDoctorEmail] = useState('');
  const [notes, setNotes] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const handleSend = async () => {
    setError(null);
    setEmailError(null);
    if (!doctorEmail || !/^\S+@\S+\.\S+$/.test(doctorEmail)) {
      setEmailError('Please enter a valid email address');
      return;
    }
    setIsLoading(true);
    try {
      const payload = {
        doctorEmail,
        doctorName: doctorName || 'Doctor',
        patientName: user?.fullName || 'Patient',
        dateRange: new Date().toLocaleDateString('en-US', {
          weekday: 'short',
          day: 'numeric',
          month: 'short',
          year: 'numeric'
        }),
        summary: JSON.stringify({
          avgBpm,
          minBpm,
          maxBpm,
          totalSessions: 1,
          totalReadings,
          notes
        })
      };
      await shareReport(token!, payload);
      setIsSuccess(true);
    } catch (err: any) {
      setError(err.message || 'Failed to send report');
    } finally {
      setIsLoading(false);
    }
  };
  const resetForm = () => {
    setDoctorName('');
    setDoctorEmail('');
    setNotes('');
    setIsSuccess(false);
    setError(null);
  };
  if (isSuccess) {
    return <main className="w-full h-full flex flex-col items-center justify-center py-12 animate-fadeInUp" data-id="element-1190" data-mp="1o6pq4rj4cuvt">
        <motion.div className="w-[80px] h-[80px] bg-accent rounded-full flex items-center justify-center shadow-elevated mb-6" initial={{
        scale: 0
      }} animate={{
        scale: 1
      }} transition={{
        type: 'spring',
        bounce: 0.6,
        duration: 0.8
      }} data-id="element-1191" data-mp="1j5a5v1ormseh">
          <motion.div initial={{
          pathLength: 0,
          opacity: 0
        }} animate={{
          pathLength: 1,
          opacity: 1
        }} transition={{
          delay: 0.4,
          duration: 0.4
        }} data-id="element-1192" data-mp="21xcq0n0m8qth">
            <Check size={32} className="text-white" strokeWidth={3} data-id="element-1193" data-mp="gglnm4yzk9to" />
          </motion.div>
        </motion.div>

        <h1 className="text-[24px] font-semibold text-text-primary mb-2" data-id="element-1194" data-mp="lbix1d06gl73">
          Report sent!
        </h1>
        <p className="text-[15px] text-text-secondary text-center max-w-[300px] mb-10" data-id="element-1195" data-mp="v3openucd4mt">
          Your report has been sent to {doctorEmail}. Your doctor will receive
          it shortly.
        </p>

        <div className="w-full max-w-[320px] space-y-4" data-id="element-1196" data-mp="144d1hd06cux2">
          <Button onClick={resetForm} variant="secondary" data-id="element-1197" data-mp="142s8hrtidk1b">
            Send Another Report
          </Button>
          <Button onClick={() => navigate(`/app/history/${sessionId}`)} data-id="element-1198" data-mp="1947qtc4axcg0">
            Back to Session
          </Button>
        </div>
      </main>;
  }
  return <main className="w-full h-full flex flex-col pb-8 max-w-[600px] mx-auto" data-id="element-1199" data-mp="3avup7yqiw9mi">
      {error && <div className="bg-danger/10 border border-danger/20 rounded-[12px] p-3 mb-6 text-danger text-[14px] font-medium text-center animate-slideDown" data-id="element-1200" data-mp="3fuxjco4ysvfp">
          {error}
        </div>}

      {/* Header */}
      <div className="flex items-center mb-6" data-id="element-1201" data-mp="155r057mpqcz0">
        <button onClick={() => navigate(`/app/history/${sessionId}`)} className="mr-4 text-text-secondary hover:text-text-primary transition-colors" aria-label="Go back" data-id="element-1202" data-mp="1j6bgzy92hypv">
          <ArrowLeft size={24} data-id="element-1203" data-mp="qhcwiuhh2khe" />
        </button>
        <h1 className="text-[20px] font-semibold text-text-primary" data-id="element-1204" data-mp="qi60xosykxwd">
          Share with Doctor
        </h1>
      </div>

      {/* Preview Card */}
      <div className="bg-white rounded-[20px] shadow-sm p-5 mb-8" data-id="element-1205" data-mp="10761hcn401co">
        <div className="flex justify-between items-center mb-4" data-id="element-1206" data-mp="109iqg9rabbz8">
          <div className="flex items-center" data-id="element-1207" data-mp="vfxq4b27y5wv">
            <div className="w-[24px] h-[24px] bg-primary rounded-full flex items-center justify-center mr-2" data-id="element-1208" data-mp="7r51z0ru92ct">
              <Heart size={12} className="text-white fill-white" data-id="element-1209" data-mp="2sbkkx9fkr7d" />
            </div>
            <span className="text-[14px] font-semibold text-text-primary tracking-[1px] uppercase" data-id="element-1210" data-mp="2q7h2gfg1u7m">
              Aurora Care
            </span>
          </div>
          <span className="text-[13px] text-text-secondary" data-id="element-1211" data-mp="2j7oqfhbmi7xr">
            Monitoring Report
          </span>
        </div>

        <div className="w-full h-[1px] bg-border mb-4" data-id="element-1212" data-mp="25cmd494g5gax" />

        <div className="space-y-2 mb-4" data-id="element-1213" data-mp="2aev97ecm8yh9">
          <p className="text-[14px] text-text-primary" data-id="element-1214" data-mp="2abpjpy02rul0">
            <span className="font-medium" data-id="element-1215" data-mp="2y4fs5x8rrvqj">Patient:</span>{' '}
            {user?.fullName || 'Patient'}
          </p>
          <p className="text-[14px] text-text-primary" data-id="element-1216" data-mp="324g5fek4u0w0">
            <span className="font-medium" data-id="element-1217" data-mp="2o5gq4xiieyci">Date:</span>{' '}
            {new Date().toLocaleDateString('en-US', {
            weekday: 'short',
            day: 'numeric',
            month: 'short',
            year: 'numeric'
          })}
          </p>
        </div>

        <div className="bg-background rounded-[12px] p-4 mb-4 flex justify-between" data-id="element-1218" data-mp="1cse9k2geu8sc">
          <div className="flex flex-col items-center" data-id="element-1219" data-mp="1hn1sllsnaazj">
            <span className="text-[11px] text-text-secondary uppercase tracking-wider mb-1" data-id="element-1220" data-mp="xlysa4qr44u8">
              Avg BPM
            </span>
            <span className="text-[16px] font-semibold text-text-primary" data-id="element-1221" data-mp="tm84m9wiu4tx">
              {avgBpm}
            </span>
          </div>
          <div className="w-[1px] bg-border" data-id="element-1222" data-mp="jvvd1xqahbea" />
          <div className="flex flex-col items-center" data-id="element-1223" data-mp="juty3i65k4ra">
            <span className="text-[11px] text-text-secondary uppercase tracking-wider mb-1" data-id="element-1224" data-mp="ewagftyldf4q">
              Min BPM
            </span>
            <span className="text-[16px] font-semibold text-text-primary" data-id="element-1225" data-mp="9wo0hcrdpks8">
              {minBpm}
            </span>
          </div>
          <div className="w-[1px] bg-border" data-id="element-1226" data-mp="5xyg307xb2z3" />
          <div className="flex flex-col items-center" data-id="element-1227" data-mp="xj3wbzdo1ip">
            <span className="text-[11px] text-text-secondary uppercase tracking-wider mb-1" data-id="element-1228" data-mp="yu3s0ppcp4j">
              Max BPM
            </span>
            <span className="text-[16px] font-semibold text-text-primary" data-id="element-1229" data-mp="1va8yyx1hfgro">
              {maxBpm}
            </span>
          </div>
        </div>

        {notes && <div className="mb-4" data-id="element-1230" data-mp="22sgu6cxoecus">
            <p className="text-[13px] font-medium text-text-primary mb-1" data-id="element-1231" data-mp="27suvzd8ooqkd">
              Notes:
            </p>
            <p className="text-[13px] text-text-secondary italic bg-background p-3 rounded-[8px]" data-id="element-1232" data-mp="2cl5ss784nw8p">
              {notes}
            </p>
          </div>}

        <p className="text-[11px] text-text-secondary italic text-center mt-6" data-id="element-1233" data-mp="1jvfdhuureob0">
          Generated by Aurora Care Fetal Monitoring
        </p>
      </div>

      {/* Form */}
      <div className="space-y-5" data-id="element-1234" data-mp="1owmk3ql61hg3">
        <div data-id="element-1235" data-mp="1ttv3fui1vrix">
          <label htmlFor="doctorName" className="block text-[13px] font-medium text-text-primary mb-2" data-id="element-1236" data-mp="1ttdap7uljvfr">
            Doctor's Name
          </label>
          <InputField id="doctorName" placeholder="Dr. Amaka Osei" icon={<User size={20} data-id="element-1238" data-mp="39a3pvw3co691" />} value={doctorName} onChange={e => setDoctorName(e.target.value)} data-id="element-1237" data-mp="3591whvw46oez" />
        </div>

        <div data-id="element-1239" data-mp="3vcfagx1tjuvg">
          <label htmlFor="doctorEmail" className="block text-[13px] font-medium text-text-primary mb-2" data-id="element-1240" data-mp="3vc71hg8th9pj">
            Doctor's Email
          </label>
          <InputField id="doctorEmail" type="email" placeholder="doctor@hospital.com" icon={<Mail size={20} data-id="element-1242" data-mp="43rnkcnxjzls" />} value={doctorEmail} onChange={e => {
          setDoctorEmail(e.target.value);
          if (emailError) setEmailError(null);
        }} error={emailError || undefined} data-id="element-1241" data-mp="8xvvoz8xmdog" />
        </div>

        <div data-id="element-1243" data-mp="ruotn8t1o2p4">
          <label htmlFor="notes" className="block text-[13px] font-medium text-text-primary mb-2" data-id="element-1244" data-mp="rt46i4927m1a">
            Add a note for your doctor (optional)
          </label>
          <textarea id="notes" className="w-full h-[100px] rounded-[12px] border border-border bg-white text-text-primary placeholder:text-text-secondary transition-all duration-300 outline-none focus:border-primary focus:shadow-focus p-4 resize-y" placeholder="e.g. Baby was very active this morning..." value={notes} onChange={e => {
          if (e.target.value.length <= 500) setNotes(e.target.value);
        }} data-id="element-1245" data-mp="2too45ryceg0r" />
          <div className={`text-right text-[12px] mt-1 ${notes.length >= 500 ? 'text-danger' : 'text-text-secondary'}`} data-id="element-1246" data-mp="zcmw4tct6diu">
            {notes.length} / 500 characters
          </div>
        </div>

        <div className="pt-4" data-id="element-1247" data-mp="bgqzri781gj6">
          <Button onClick={handleSend} isLoading={isLoading} loadingText="Sending..." data-id="element-1248" data-mp="bhk4kdmmdvyo">
            <div className="flex items-center justify-center space-x-2" data-id="element-1249" data-mp="gg3uep4c3rwb">
              <Send size={18} data-id="element-1250" data-mp="25w3ayraa8jlz" />
              <span data-id="element-1251" data-mp="25sp71elz02cf">Send Report</span>
            </div>
          </Button>
        </div>
      </div>
    </main>;
}